import React, { useState } from 'react';
import {
  Stack,
  Typography,
} from '@mui/material';
import { useFetch } from 'use-http';
import PdfIndicator from '../../../../common/atoms/pdfIndicator/PdfIndicator';
import WholeMeProfileSection from '../wholeMeProfileSection/WholeMeProfileSection';
import { DocumentIcon } from '../../icons/Icons';
import WholeMeUploadManagerModal from './WholeMeUploadManagerModal';
import FileUploadButton from '../../../../common/atoms/fileUploadButton/FileUploadButton';
import BaseModal from '../../../../common/atoms/BaseModal/BaseModal';
import BackrsButton from '../../../../common/atoms/backrsButton/BackrsButton';
import appStrings from '../../../../common/app_strings';
import appColors from '../../../../common/app_colors';

const { text, buttons } = appColors;

const styles = {
  stack: {
    alignItems: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '200px',
    maxwidth: '200px',
    minwidth: '150px',
  },
  typography: {
    fontSize: '14px',
    color: text.darkTaupe,
  },
  buttonStylesOverride: {
    maxWidth: '140px',
    maxHeight: '45px',
    fontWeight: '600',
  },
};

const WholeMeProfileDocumentSection = ({
  documents, editable, onDocumentDelete, onDocumentPost,
}) => {
  const [open, setOpen] = useState(false);
  const [confirmModelOpen, setConfirmModelOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [mode, setMode] = useState(null);
  const { delete: del } = useFetch();
  const { wholeMeProfileStory: { documentSection, deleting } } = appStrings;

  const openModal = () => {
    setOpen(true);
    setMode('documents');
  };

  const handleClose = () => {
    setOpen(false);
    setMode(null);
  };

  const handleConfirmModalOpen = () => {
    setConfirmModelOpen(true);
  };

  const handleConfirmModalClose = () => {
    setConfirmModelOpen(false);
  };

  const handleDocumentDeleteSet = (document) => {
    setDocumentToDelete(document);
    handleConfirmModalOpen();
  };

  const handleDocumentDelete = async (document) => {
    try {
      const resp = await del(`/users-profile-answers/${document.id}`);
      if (!resp) {
        throw new Error('handleDocumentDelete: Failed to delete document');
      }
      onDocumentDelete();
    } catch (error) {
      console.error('Failed to delete document: ', error);
    } finally {
      handleConfirmModalClose();
    }
  };

  const renderDocuments = () => (
    documents?.map((document) => (
      <PdfIndicator
        key={document.id}
        attachment={{
          name: document?.title,
          description: document?.description,
          uri: document?.media_url,
          type: document?.media_type,
        }}
        editable={editable}
        onDelete={() => handleDocumentDeleteSet(document)}
        overrideTextStyle={{
          textAlign: 'left',
          flexGrow: 0,
          maxWidth: '8em',
        }}
        overrideStyle={{
          justifyContent: 'space-between',
          width: {
            xs: '170px',
            sm: '150px',
            md: '200px',
          },
          maxWidth: {
            xs: '170px',
            md: '200px',
          },
          minWidth: '100px',
        }}
      />
    ))
  );

  return (
    <>
      <WholeMeProfileSection
        title="Documents"
        titleIcon={<DocumentIcon styles={{ height: '100%' }} />}
        titleSx={{
          fontSize: '24px',
        }}
        expanded
        editable={editable}
        showExpandedEditIcon
        disableExpand
        expandedBorder={false}
        iconStylesOverride={{ maxHeight: '67px' }}
      >
        <Stack spacing={1.5} sx={{ pb: '15px' }} id="wholeme-document-section-container">
          {!editable && (
          <Stack
            sx={styles.stack}
          >
            <Typography sx={styles.typography}>
              {documentSection.subText}
            </Typography>
          </Stack>
          )}
          <Stack
            spacing={1.5}
            sx={{
              ...styles.stack,
              paddingTop: '5px',
            }}
          >
            {renderDocuments()}
            {editable && (
            <Stack>
              <FileUploadButton onClick={openModal} label={documentSection.uploadButton.label} onDocumentPost={onDocumentPost} />
            </Stack>
            )}
          </Stack>
        </Stack>
      </WholeMeProfileSection>
      <WholeMeUploadManagerModal
        type="document"
        mode={mode}
        isOpen={open}
        handleClose={handleClose}
        onDocumentPost={onDocumentPost}
      />
      <BaseModal
        isOpen={confirmModelOpen}
        handleClose={handleConfirmModalClose}
      >
        <Stack spacing={1.5} sx={{ padding: '10px' }}>
          <Typography>
            {deleting.areYouSure}
            {' '}
            {documentToDelete?.title}
            ?
          </Typography>
          <Stack
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: '25px',
            }}
          >
            <BackrsButton
              onClick={() => { handleDocumentDelete(documentToDelete); }}
              buttonStylesOverride={{
                ...styles.buttonStylesOverride,
                backgroundColor: buttons.darkNavy,
                '&:hover': { backgroundColor: buttons.darkNavy },
              }}
              label={deleting.confirm}
            />
            <BackrsButton
              onClick={handleConfirmModalClose}
              buttonStylesOverride={{
                ...styles.buttonStylesOverride,
                backgroundColor: buttons.warnigBackground,
                '&:hover': { backgroundColor: buttons.warnigBackground },
              }}
              label={deleting.cancel}
            />
          </Stack>
        </Stack>
      </BaseModal>
    </>
  );
};

export default WholeMeProfileDocumentSection;
